import { newFrameElement } from "@excalidraw/excalidraw/element/newElement";
import { syncInvalidIndices } from "@excalidraw/excalidraw/fractionalIndex";
import { isFrameLikeElement } from "@excalidraw/excalidraw/element/typeChecks";

import { readFrameMeta, writeFrameMeta } from "./frameMeta";

import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

/** A4 landscape ratio (297:210), so a frame fills one exported PDF page.
 * Big enough for a drawing, small enough that five fit across a screen. */
export const FRAME_WIDTH = 840;
export const FRAME_HEIGHT = 594;
export const FRAME_GAP = 120;
export const FRAMES_PER_ROW = 5;

/** Half a frame between groups: room to tell them apart, still one screen. */
export const GROUP_FRAME_GAP_X = FRAME_WIDTH / 2;
export const GROUP_FRAME_GAP_Y = FRAME_HEIGHT / 2;

export const frameAt = (
  index: number,
  name: string,
  framesPerRow: number = FRAMES_PER_ROW,
  gapX: number = FRAME_GAP,
  gapY: number = FRAME_GAP,
): ExcalidrawElement =>
  newFrameElement({
    name,
    x: (index % framesPerRow) * (FRAME_WIDTH + gapX),
    y: Math.floor(index / framesPerRow) * (FRAME_HEIGHT + gapY),
    width: FRAME_WIDTH,
    height: FRAME_HEIGHT,
  });

export type RosterEntry = { studentNumber: number; displayName: string | null };

/**
 * A new frame has no fractional index, and the server orders a scene by
 * index only once *every* element has one. A frame nobody ever touched kept
 * the whole canvas in arrival order instead: every screen that opened it
 * afterwards re-numbered the lot locally, so a student's "send to back"
 * landed in a different place on each screen and an opaque background hid
 * their drawing for part of the class (live class, 10-13).
 */
const indexed = (frames: ExcalidrawElement[]): ExcalidrawElement[] =>
  syncInvalidIndices(frames);

export const buildIndividualFrames = (
  roster: readonly RosterEntry[],
): ExcalidrawElement[] =>
  indexed(
    [...roster]
      .sort((left, right) => left.studentNumber - right.studentNumber)
      .map((entry, index) => {
        const label = entry.displayName
          ? `${entry.studentNumber}번 ${entry.displayName}`
          : `${entry.studentNumber}번`;
        return writeFrameMeta(frameAt(index, label), {
          owner: entry.studentNumber,
          sealed: null,
          comments: [],
        });
      }),
  );

/** Near-square grid so a handful of groups still fits one screen. */
export const groupFramesPerRow = (count: number): number =>
  Math.max(1, Math.ceil(Math.sqrt(count)));

/**
 * Group frames carry no owner on purpose: membership changes every lesson, so
 * the spec drops the "can I touch a groupmate's work" rule entirely inside
 * them. See `studentChangePolicy` (contracts) — a canvas with no owner tag gets
 * no protection at all.
 */
export const buildGroupFrames = (
  count: number,
  gapX: number = GROUP_FRAME_GAP_X,
  gapY: number = GROUP_FRAME_GAP_Y,
): ExcalidrawElement[] =>
  indexed(
    Array.from({ length: count }, (_, index) =>
      writeFrameMeta(
        frameAt(
          index,
          `${index + 1}모둠`,
          groupFramesPerRow(count),
          gapX,
          gapY,
        ),
        {
          owner: null,
          sealed: null,
          comments: [],
        },
      ),
    ),
  );

/** The class size the teacher's opening zoom is fixed to, whatever the real
 * count: a smaller class sits comfortably inside it, a bigger one runs past. */
export const OVERVIEW_STUDENTS = 24;
export const OVERVIEW_GROUPS = 6;
/** Room around the reference grid, also clearing the toolbars. */
const OVERVIEW_PADDING = 1.15;

const gridExtent = (
  count: number,
  perRow: number,
  gapX: number,
  gapY: number,
) => {
  const cols = Math.min(count, perRow);
  const rows = Math.ceil(count / perRow);
  return {
    width: cols * FRAME_WIDTH + (cols - 1) * gapX,
    height: rows * FRAME_HEIGHT + (rows - 1) * gapY,
  };
};

/**
 * The teacher's opening view: one zoom per canvas kind (individual frames
 * carry an owner, group frames don't) sized so a 24-student or 6-group grid
 * fits the viewport, centered on the frames actually there. Null when there
 * are no frames to center on.
 */
export const overviewViewport = (
  elements: readonly ExcalidrawElement[],
  viewport: { width: number; height: number },
): { zoom: number; scrollX: number; scrollY: number } | null => {
  const frames = elements.filter(
    (element) => isFrameLikeElement(element) && !element.isDeleted,
  );
  if (frames.length === 0 || viewport.width <= 0 || viewport.height <= 0) {
    return null;
  }
  const individual = frames.some(
    (frame) => readFrameMeta(frame).owner !== null,
  );
  const reference = individual
    ? gridExtent(OVERVIEW_STUDENTS, FRAMES_PER_ROW, FRAME_GAP, FRAME_GAP)
    : gridExtent(
        OVERVIEW_GROUPS,
        groupFramesPerRow(OVERVIEW_GROUPS),
        GROUP_FRAME_GAP_X,
        GROUP_FRAME_GAP_Y,
      );
  const zoom = Math.min(
    1,
    viewport.width / (reference.width * OVERVIEW_PADDING),
    viewport.height / (reference.height * OVERVIEW_PADDING),
  );
  const minX = Math.min(...frames.map((frame) => frame.x));
  const minY = Math.min(...frames.map((frame) => frame.y));
  const maxX = Math.max(...frames.map((frame) => frame.x + frame.width));
  const maxY = Math.max(...frames.map((frame) => frame.y + frame.height));
  return {
    zoom,
    scrollX: viewport.width / 2 / zoom - (minX + maxX) / 2,
    scrollY: viewport.height / 2 / zoom - (minY + maxY) / 2,
  };
};

export const findMyFrame = (
  elements: readonly ExcalidrawElement[],
  studentNumber: number | null,
): ExcalidrawElement | null => {
  if (studentNumber === null) {
    return null;
  }
  const frames = elements.filter(isFrameLikeElement);
  if (frames.some((frame) => readFrameMeta(frame).owner !== null)) {
    return (
      frames.find((frame) => readFrameMeta(frame).owner === studentNumber) ??
      null
    );
  }
  return (
    frames.find((frame) =>
      frame.name?.trim().startsWith(`${studentNumber}번`),
    ) ?? null
  );
};
